import { Slider, SliderProps, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  label: {
    color: theme.palette.text.secondary,
    lineHeight: 1,
    transform: "translate(0, 1.5px) scale(0.75)",
    transformOrigin: "top left",
  },
}));

type Props = {
  title: string;
  value: number | undefined;
  tooltip?: string;
  disabledText?: string;
  disableMarks?: boolean;
  onChange: (newVal: number) => void;
} & Omit<SliderProps, "value" | "onChange">;

const AdjustmentSlider = ({
  title,
  value,
  onChange,
  tooltip = "",
  disableMarks = false,
  disabledText,
  ...sliderProps
}: Props): JSX.Element => {
  const { t } = useTranslation("cesium");
  const classes = useStyles();
  const disabled = React.useMemo(() => Number.isNaN(value), [value]);

  const handleChange = React.useCallback(
    (event: Event, v: number | number[]) => {
      if (!(typeof v === "number") || !event) return;
      onChange(v);
    },
    [onChange],
  );

  const disText = React.useMemo(
    () => disabledText ?? t("unable-to-adjust").toString(),
    [disabledText, t],
  );

  const marks = React.useMemo(() => {
    if (!Number.isNaN(sliderProps.min) && !Number.isNaN(sliderProps.max)) {
      const ret: SliderProps["marks"] = [];
      ret.push({
        value: sliderProps.min!,
        label: sliderProps.min!,
      });
      ret.push({
        value: sliderProps.max!,
        label: sliderProps.max!,
      });
      return ret;
    }
    return undefined;
  }, [sliderProps.min, sliderProps.max]);

  return (
    <>
      <Typography gutterBottom variant="body1" className={classes.label}>
        {title}
      </Typography>
      <Tooltip title={disabled ? disText : tooltip}>
        <span>
          <Slider
            disabled={disabled}
            color="secondary"
            value={value ?? 0}
            onChange={handleChange}
            valueLabelDisplay="auto"
            marks={!disableMarks && marks ? marks : undefined}
            {...sliderProps}
          />
        </span>
      </Tooltip>
    </>
  );
};

export default AdjustmentSlider;
